/**
 * Hook: fetch the current principal via GET /api/v1/me.
 *
 * With no live session there is nothing to ask: the query resolves to no
 * principal without a request, and the login page sends the browser to
 * the realm. A 401 or 403 means the API refused a token the app did send;
 * its reason is kept so the login page can say so, instead of blaming the
 * API whenever the token was simply gone.
 */
import { useQuery } from '@tanstack/react-query';

import { ApiError, resolveAuthToken } from '@optum/api-client/client';
import { getMe } from '@optum/api-client/endpoints/me';
import type { Me } from '@optum/api-client/types';

import { qk } from '../lib/query-keys.js';

/** What `/me` answered: the principal, or why the API refused the token. */
interface MeResult {
  me: Me | null;
  refusal: string | null;
}

export interface CurrentAdmin {
  user: Me | null;
  /** The API's reason when it refused a token the app sent; null otherwise. */
  refusal: string | null;
  /** `/me` failed for another reason (the API down, the network gone). */
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  /** Asks `/me` again. */
  retry: () => void;
}

/** The principal behind the live session, and whether the API refused it. */
export function useCurrentAdmin(): CurrentAdmin {
  const query = useQuery({
    queryKey: qk.me(),
    queryFn: async (): Promise<MeResult> => {
      if (!(await resolveAuthToken())) return { me: null, refusal: null };
      try {
        return { me: await getMe(), refusal: null };
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return { me: null, refusal: err.message };
        }
        throw err;
      }
    },
    refetchOnWindowFocus: true,
  });

  return {
    user: query.data?.me ?? null,
    refusal: query.data?.refusal ?? null,
    isError: query.isError,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    retry: () => void query.refetch(),
  };
}

export function isInternal(user: Me | null): boolean {
  return (
    user?.platform_role === 'optum-platform-admin' ||
    user?.platform_role === 'optum-platform-viewer'
  );
}

export function isPlatformAdmin(user: Me | null): boolean {
  return user?.platform_role === 'optum-platform-admin';
}
